/**
 * WebUSB driver for Star TSP700II / TSP743II thermal printers.
 *
 * Sends raw ESC/POS byte buffers over USB bulk transfers.
 * Large payloads are chunked to avoid overflowing the printer's
 * input buffer (4096 bytes with a 10ms inter-chunk delay).
 */

import { PRINTER } from '../lib/constants';
import { EscPos } from '../lib/escpos';
import type { PrintData, TicketFields } from './types';

export class PrinterUsb {
  private device: USBDevice | null = null;
  private endpointNumber: number | null = null;
  private connected = false;

  // ---------- Connection lifecycle ----------

  /**
   * Prompt the user to select a Star printer USB device,
   * open it, claim the bulk interface, and locate the OUT endpoint.
   */
  async connect(): Promise<boolean> {
    try {
      this.device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: PRINTER.VENDOR_ID, productId: PRINTER.PRODUCT_ID },
        ],
      });

      await this.device.open();
      await this.device.selectConfiguration(1);
      await this.device.claimInterface(0);

      // Find the first OUT bulk endpoint on interface 0
      const iface = this.device.configuration?.interfaces[0];
      const alternate = iface?.alternate;
      const outEndpoint = alternate?.endpoints.find(
        (ep) => ep.direction === 'out' && ep.type === 'bulk',
      );

      if (!outEndpoint) {
        console.error('[Printer] No OUT bulk endpoint found');
        await this.disconnect();
        return false;
      }

      this.endpointNumber = outEndpoint.endpointNumber;
      this.connected = true;
      return true;
    } catch (err) {
      console.error('[Printer] connect failed:', err);
      this.connected = false;
      return false;
    }
  }

  /** Release the USB interface and close the device. */
  async disconnect(): Promise<void> {
    try {
      if (this.device) {
        await this.device.releaseInterface(0).catch(() => {});
        await this.device.close().catch(() => {});
        this.device = null;
      }
    } finally {
      this.endpointNumber = null;
      this.connected = false;
    }
  }

  // ---------- Printing ----------

  /**
   * Send a raw byte buffer to the printer, splitting into
   * CHUNK_SIZE segments with CHUNK_DELAY between each.
   */
  async printRaw(data: Uint8Array): Promise<boolean> {
    if (!this.device || this.endpointNumber === null) return false;

    try {
      let offset = 0;
      while (offset < data.length) {
        const end = Math.min(offset + PRINTER.CHUNK_SIZE, data.length);
        const chunk = data.slice(offset, end);
        await this.device.transferOut(this.endpointNumber, chunk);
        offset = end;

        if (offset < data.length) {
          await this.delay(PRINTER.CHUNK_DELAY);
        }
      }
      return true;
    } catch (err) {
      console.error('[Printer] printRaw failed:', err);
      return false;
    }
  }

  /**
   * Print ticket data received from the backend.
   * Supports single tickets (print_data) and multi-ticket batches.
   */
  async printTicket(printData: PrintData): Promise<boolean> {
    if (!this.device || this.endpointNumber === null) return false;

    try {
      if (printData.multi_tickets && printData.tickets?.length) {
        // Sort tickets by index and print each one
        const sorted = [...printData.tickets].sort((a, b) => a.index - b.index);
        for (const ticket of sorted) {
          const bytes = this.base64ToBytes(ticket.data);
          const ok = await this.printRaw(bytes);
          if (!ok) return false;
        }
        return true;
      }

      // Single ticket
      const bytes = this.base64ToBytes(printData.print_data);
      return await this.printRaw(bytes);
    } catch (err) {
      console.error('[Printer] printTicket failed:', err);
      return false;
    }
  }

  /**
   * Build and print a ticket from structured fields using ESC/POS commands.
   *
   * Layout:
   *   [center, double size] Establishment name
   *   [center, normal]      Address
   *   [left]                SIRET / Version / Caisse
   *   ================================
   *   Date / Time
   *   [bold, double height] Product + Price
   *   ================================
   *   [center]              EAN-13 barcode
   *   [feed + cut]
   */
  async printFromTemplate(fields: TicketFields): Promise<boolean> {
    const separator = '================================';

    const ticket = new EscPos()
      // Header
      .init()
      .center()
      .doubleSize(true)
      .text(fields.etablissement)
      .doubleSize(false)
      .text(fields.adresse)
      .emptyLine()

      // Business info
      .left()
      .text(`SIRET: ${fields.siret}`)
      .text(`Version: ${fields.version}`)
      .text(`Caisse: ${fields.caisse}`)
      .text(separator)

      // Date & ticket number
      .text(`Date: ${fields.date}  Heure: ${fields.heure}`)
      .text(`Ticket: ${fields.numero_ticket}`)
      .text(separator)

      // Product
      .bold(true)
      .doubleHeight(true)
      .text(fields.produit)
      .text(`${fields.prix} EUR`)
      .doubleHeight(false)
      .bold(false)
      .text(separator)

      // Barcode
      .emptyLine()
      .center()
      .barcode(fields.code_barre)

      // Feed and cut
      .feed(5)
      .cut()
      .toBytes();

    return await this.printRaw(ticket);
  }

  /**
   * Print a short test receipt to verify connectivity.
   * Prints a header, the current date/time, and cuts the paper.
   */
  async testPrint(): Promise<boolean> {
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR');
    const timeStr = now.toLocaleTimeString('fr-FR');

    const ticket = new EscPos()
      .init()
      .center()
      .doubleSize(true)
      .text('=== TEST BORNE ===')
      .doubleSize(false)
      .emptyLine()
      .text(`Date: ${dateStr}`)
      .text(`Heure: ${timeStr}`)
      .emptyLine()
      .text('Impression OK')
      .feed(5)
      .cut()
      .toBytes();

    return await this.printRaw(ticket);
  }

  // ---------- State ----------

  get isConnected(): boolean {
    return this.connected;
  }

  // ========== Private helpers ==========

  /** Decode a base64 string into a Uint8Array. */
  private base64ToBytes(b64: string): Uint8Array {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /** Promise-based delay. */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
